/**
 * ReferralService — handles referral code management, tracking, and XP rewards
 */
import { supabase } from '@/integrations/supabase/client';
import { checkAndAwardBadgesForEvent } from '@/lib/badges';
import { trackEvent } from '@/lib/analytics';
import type { StreakState } from '@/lib/gamification';

const REFERRAL_CODE_KEY = 'wren:pending-referral';
const XP_REWARD_REFERRER = 100;
const XP_REWARD_REFERRED = 50;

export interface ReferralStats {
  referralCode: string;
  totalReferred: number;
  referrals: Array<{ created_at: string; status: string }>;
}

class ReferralService {
  /**
   * Save referral code from URL params to localStorage (pre-auth)
   */
  captureReferralCode(): string | null {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (code) {
      localStorage.setItem(REFERRAL_CODE_KEY, code);
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }
    return code || localStorage.getItem(REFERRAL_CODE_KEY);
  }

  /**
   * Get the user's own referral code
   */
  async getMyReferralCode(userId: string): Promise<string | null> {
    const { data } = await supabase
      .from('profiles')
      .select('referral_code')
      .eq('user_id', userId)
      .maybeSingle();
    return (data as any)?.referral_code || null;
  }

  /**
   * Get referral stats for the current user
   */
  async getReferralStats(userId: string): Promise<ReferralStats> {
    const [codeRes, referralsRes] = await Promise.all([
      this.getMyReferralCode(userId),
      supabase
        .from('referrals')
        .select('created_at, status')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false }),
    ]);

    return {
      referralCode: codeRes || '',
      totalReferred: referralsRes.data?.length || 0,
      referrals: (referralsRes.data as any[]) || [],
    };
  }

  /**
   * Process pending referral after user signs up/in
   * Called once after authentication
   */
  async processPendingReferral(userId: string): Promise<boolean> {
    const pendingCode = localStorage.getItem(REFERRAL_CODE_KEY);
    if (!pendingCode) return false;

    try {
      // Find the referrer by code
      const { data: referrerProfile } = await supabase
        .from('profiles')
        .select('user_id, referral_code')
        .eq('referral_code', pendingCode)
        .maybeSingle();

      if (!referrerProfile) {
        localStorage.removeItem(REFERRAL_CODE_KEY);
        return false;
      }

      const referrerId = (referrerProfile as any).user_id;

      // Don't self-refer
      if (referrerId === userId) {
        localStorage.removeItem(REFERRAL_CODE_KEY);
        return false;
      }

      // Check if already referred
      const { data: existing } = await supabase
        .from('referrals')
        .select('id')
        .eq('referred_id', userId)
        .maybeSingle();

      if (existing) {
        localStorage.removeItem(REFERRAL_CODE_KEY);
        return false;
      }

      // Record the referral
      const { error: refError } = await supabase
        .from('referrals')
        .insert({
          referrer_id: referrerId,
          referred_id: userId,
          status: 'completed',
        });

      if (refError) {
        console.error('Failed to record referral:', refError);
        return false;
      }

      // Update referred_by on profile
      await supabase
        .from('profiles')
        .update({ referred_by: referrerId } as any)
        .eq('user_id', userId);

      // Award XP to referrer
      await this.awardReferralXP(referrerId, XP_REWARD_REFERRER);
      // Award XP to referred user
      await this.awardReferralXP(userId, XP_REWARD_REFERRED);

      // Check for connector badge for the referrer
      try {
        const { data: progress } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', referrerId)
          .maybeSingle();
        if (progress) {
          await checkAndAwardBadgesForEvent(referrerId, 'referral_completed', {
            ...progress,
            streak_state: (progress.streak_state as StreakState) ?? 'active',
            streaks_broken_count: progress.streaks_broken_count ?? 0,
            coffee_count: progress.coffee_count ?? 0,
            max_merchant_count: progress.max_merchant_count ?? 0,
            recurring_count: progress.recurring_count ?? 0,
            months_under_budget: (progress.months_under_budget as string[]) ?? [],
          });
        }
      } catch (e) {
        console.error('Badge check for referral failed:', e);
      }

      // Mark XP as awarded
      await supabase
        .from('referrals')
        .update({ xp_awarded: true } as any)
        .eq('referred_id', userId);

      localStorage.removeItem(REFERRAL_CODE_KEY);
      return true;
    } catch (error) {
      console.error('Error processing referral:', error);
      return false;
    }
  }

  /**
   * Award XP to a user for referral
   */
  private async awardReferralXP(userId: string, amount: number): Promise<void> {
    const { data: progress } = await supabase
      .from('user_progress')
      .select('xp, level')
      .eq('user_id', userId)
      .maybeSingle();

    if (progress) {
      const newXp = (progress.xp || 0) + amount;
      const newLevel = Math.floor(newXp / 100) + 1;
      await supabase
        .from('user_progress')
        .update({ xp: newXp, level: newLevel })
        .eq('user_id', userId);
    }
  }

  /**
   * Build the referral share URL
   */
  getReferralUrl(code: string): string {
    return `https://trywren.app?ref=${code}`;
  }
}

export const referralService = new ReferralService();
